import { useState, useRef, useEffect } from "react";
import { sendChatMessage } from "../services/api";
import { useCart } from "../context/CartContext";
import { imageSrc } from "../utils/assets";
import styles from "./ChatPage.module.css";

const channels = [
  { icon: "📱", name: "WhatsApp", desc: "Chat instantly with our stylists", detail: "+20 108 097 2433", btnClass: styles.whatsapp, btnLabel: "Open WhatsApp", href: "https://wa.me/201080972433?text=Hello%20Atelier%20Noir", response: "5 min" },
  { icon: "📘", name: "Facebook", desc: "Message us on Facebook", detail: "@AtelierNoir", btnClass: styles.facebook, btnLabel: "Open Messenger", href: "https://m.me/ateliernoir", response: "15 min" },
  { icon: "📷", name: "Instagram", desc: "DM us on Instagram", detail: "@ateliernoir", btnClass: styles.instagram, btnLabel: "Open Instagram", href: "https://instagram.com/ateliernoir", response: "30 min" },
];

export default function ChatPage() {
  const { addToCart } = useCart();
  const [messages, setMessages] = useState([
    { role: "bot", text: "👋 Welcome to Atelier Noir. I'm your personal style concierge — how can I help today?" },
    { role: "bot", text: "I can assist with sizing, styling, orders, tracking, alterations, and more." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    const msg = input.trim();
    if (!msg || loading) return;
    setMessages((prev) => [...prev, { role: "user", text: msg }]);
    setInput("");
    setLoading(true);
    try {
      const { reply, conversationId: cid, products } = await sendChatMessage(msg, conversationId);
      setConversationId(cid);
      setMessages((prev) => [...prev, { role: "bot", text: reply, products }]);
    } catch {
      setMessages((prev) => [...prev, { role: "bot", text: "Sorry, I encountered an error. Please try again." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className="container">
        <h1 className={styles.title}>Style Concierge</h1>
        <div className={styles.layout}>
          <div className={styles.chatCard}>
            <div className={styles.chatHeader}>
              <div className={styles.onlineDot} />
              <span>Atelier Noir Concierge</span>
            </div>
            <div className={styles.messages}>
              {messages.map((m, i) => (
                <div key={i} className={styles.msgGroup}>
                  <div className={`${styles.message} ${m.role === "user" ? styles.user : styles.bot}`}>{m.text}</div>
                  {m.products && m.products.length > 0 && (
                    <div className={styles.products}>
                      {m.products.map((p) => (
                        <div key={p.id} className={styles.prodCard}>
                          {p.image_url && <img className={styles.prodImg} src={imageSrc(p.image_url)} alt={p.name} loading="lazy" />}
                          <div className={styles.prodInfo}>
                            <p className={styles.prodName}>{p.name.split(" / ")[0]}</p>
                            <p className={styles.prodMeta}>{p.color ? `${p.color} · ` : ""}EGP {Number(p.price).toLocaleString()}</p>
                            <button className={styles.prodBtn} onClick={() => addToCart({ ...p, emoji: "" }, p.price)}>
                              Add to Bag
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {loading && (
                <div className={`${styles.message} ${styles.bot} ${styles.loadingMsg}`}>
                  <span className="loading-dot" /><span className="loading-dot" /><span className="loading-dot" />
                </div>
              )}
              <div ref={endRef} />
            </div>
            <div className={styles.inputRow}>
              <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Type your message..." disabled={loading} />
              <button className={styles.sendBtn} onClick={send} disabled={loading}>➤</button>
            </div>
          </div>

          <div className={styles.sidebar}>
            <div className={styles.sideCard}>
              <h3>📱 Connect with Us</h3>
              <p>Choose your preferred channel</p>
              <div className={styles.channels}>
                {channels.map((ch) => (
                  <div key={ch.name} className={styles.channelCard}>
                    <div className={styles.channelTop}>
                      <span className={styles.channelIcon}>{ch.icon}</span>
                      <div>
                        <p className={styles.channelName}>{ch.name}</p>
                        <p className={styles.channelDesc}>{ch.desc}</p>
                        <p className={styles.channelDetail}>{ch.detail}</p>
                      </div>
                    </div>
                    <button className={`${styles.channelBtn} ${ch.btnClass}`} onClick={() => window.open(ch.href, "_blank")}>{ch.btnLabel}</button>
                  </div>
                ))}
              </div>
            </div>
            <div className={styles.responseTimes}>
              <h4>⏱️ Response Times</h4>
              {channels.map((ch) => (
                <div key={ch.name} className={styles.responseRow}>
                  <span>🟢 {ch.name}</span><span>~{ch.response}</span>
                </div>
              ))}
              <div className={styles.responseRow}><span>🟢 Chat Bot</span><span>Instant 24/7</span></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}